import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import CheckinAssetsTable from '../../components/checkin/CheckinAssetsTable';
import CheckinForm from '../../components/checkin/CheckinForm';
import CheckinSidePanel from '../../components/checkin/CheckinSidePanel';
import CheckinEmailSection from '../../components/checkin/CheckinEmailSection';
import Loader from '../../components/common/Loader';
import { PERMISSIONS, canFeatureForRole } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import '../../styles/Forms.css';

const CheckinFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  
  const [formData, setFormData] = useState({
    checkinFrom: 'person', // 'person' or 'site'
    returnDate: new Date().toISOString().split('T')[0],
    condition: 'good', // 'good', 'damaged', 'repair_needed'
    site: '',
    location: '',
    department: '',
    notes: '',
    sendEmail: false,
    emailAddresses: '',
  });
  const [checkinPhotos, setCheckinPhotos] = useState([]);
  const [photosMandatory, setPhotosMandatory] = useState(false);

  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }, []);
  const allowCheckin = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.CHECKIN]),
    [normalizedUserRole, rolePermissions]
  );

  useEffect(() => {
    if (!permissionsReady) return;
    if (!allowCheckin) {
      toast.error('You do not have permission to check in assets');
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    if (location.state && location.state.selectedAssets) {
      setSelectedAssets(location.state.selectedAssets);
    } else {
      toast.error('No assets selected');
      navigate('/admin/checkin');
      return;
    }

    fetchInitialData();
  }, [location.state, navigate, permissionsReady, allowCheckin]);

  // Clear photos when multiple assets are selected (photos only allowed for single asset)
  useEffect(() => {
    if (selectedAssets.length > 1 && checkinPhotos.length > 0) {
      setCheckinPhotos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssets.length]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch settings to check if photos are mandatory
      try {
        const settingsRes = await api.getSettings();
        if (settingsRes.success && settingsRes.data) {
          setPhotosMandatory(settingsRes.data.checkinPhotosMandatory || false);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
      
      // Fetch sites
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      // Fetch departments
      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }

      // Fetch locations
      const locationsRes = await api.getLocations({ isActive: 'true' });
      if (locationsRes.success) {
        setLocations(locationsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAsset = (assetId) => {
    const updatedAssets = selectedAssets.filter(asset => {
      const id = asset.id || asset._id;
      return id !== assetId;
    });
    setSelectedAssets(updatedAssets);
    // Clear photos if multiple assets remain (photos only allowed for single asset)
    if (updatedAssets.length > 1 && checkinPhotos.length > 0) {
      setCheckinPhotos([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSiteChange = (siteId) => {
    setFormData(prev => ({ ...prev, site: siteId }));
  };

  const handleLocationChange = (locationName) => {
    setFormData(prev => ({ ...prev, location: locationName }));
  };

  const handleDepartmentChange = (deptId) => {
    setFormData(prev => ({ ...prev, department: deptId }));
  };

  const handleNotesChange = (value) => {
    setFormData(prev => ({ ...prev, notes: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedAssets.length === 0) {
      toast.error('Please select at least one asset to check in');
      return;
    }

    // Validate photos if mandatory (only for single asset checkin)
    if (selectedAssets.length === 1 && photosMandatory && checkinPhotos.length === 0) {
      toast.error('Please upload at least one photo for checkin');
      return;
    }

    try {
      setLoading(true);
      
      // Checkin each asset with skipEmail flag
      const checkinPromises = selectedAssets.map(asset => {
        const assetId = asset.id || asset._id;
        
        // Build location object - only include fields that have values (changed by admin)
        // If no fields are changed, don't send location object at all to preserve original values
        const locationUpdate = {};
        if (formData.site) locationUpdate.site = formData.site;
        if (formData.department) locationUpdate.department = formData.department;
        if (formData.location) locationUpdate.locationDetail = formData.location;
        
        // Only pass photos for single asset checkin
        const photosToSend = selectedAssets.length === 1 ? checkinPhotos : [];
        
        return api.checkinAsset(assetId, {
          condition: formData.condition || 'good',
          location: Object.keys(locationUpdate).length > 0 ? locationUpdate : undefined,
          notes: formData.notes,
          skipEmail: true, // Skip individual emails, will send consolidated email
        }, photosToSend); // Pass photos to API only for single asset
      });

      const checkinResults = await Promise.all(checkinPromises);
      
      // Send consolidated email after all checkins complete
      try {
        const successfulAssets = checkinResults
          .filter((result, index) => result.success)
          .map((result, index) => selectedAssets[checkinResults.findIndex(r => r === result)]);
        
        if (successfulAssets.length > 0) {
          const assetIds = successfulAssets.map(asset => asset.id || asset._id);
          await api.sendCheckinEmail({
            emailAddresses: '', // Will be determined by backend
            assetIds,
            condition: formData.condition || undefined,
            returnDate: formData.returnDate || undefined,
            notes: formData.notes || undefined,
          });
        }
      } catch (error) {
        console.error('Error sending consolidated checkin email:', error);
        // Don't fail the checkin if email fails
      }

      // Send email if enabled
      if (formData.sendEmail && formData.emailAddresses) {
        try {
          const assetIds = selectedAssets.map(asset => asset.id || asset._id);
          await api.sendCheckinEmail({
            emailAddresses: formData.emailAddresses,
            assetIds,
            condition: formData.condition || undefined,
            returnDate: formData.returnDate || undefined,
            notes: formData.notes || undefined,
          });
          toast.success('Check-in email notification sent');
        } catch (error) {
          console.error('Error sending check-in email:', error);
          toast.error('Error sending email notification: ' + (error.message || 'Unknown error'));
        }
      }

      toast.success('Assets checked in successfully!');
      navigate('/admin/assets');
    } catch (error) {
      console.error('Checkin error:', error);
      toast.error('Error checking in assets: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (!permissionsReady) {
    return <Loader />;
  }
  if (!allowCheckin) {
    return null;
  }

  if (loading && selectedAssets.length === 0) {
    return <Loader />;
  }

  if (selectedAssets.length === 0) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{
          background: 'white',
          padding: '60px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📥</div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>
            No Assets Selected
          </h3>
          <p style={{ margin: 0, color: '#718096', fontSize: '13px', marginBottom: '20px' }}>
            Please go back and select assets for check-in
          </p>
          <button
            onClick={() => navigate('/admin/checkin')}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Go to Check-In Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin-form-page" style={{ padding: '16px' }}>
      {/* Selected Assets Table */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Selected Assets</h2>
        <CheckinAssetsTable
          assets={selectedAssets}
          onRemove={handleRemoveAsset}
        />
      </div>

      {/* Form Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Left Panel - Form */}
        <div>
          <div style={{ 
            background: 'white', 
            padding: '16px', 
            borderRadius: '8px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '16px'
          }}>
            <CheckinForm
              formData={formData}
              sites={sites}
              onChange={handleInputChange}
              photos={checkinPhotos}
              onPhotosChange={setCheckinPhotos}
              photosMandatory={photosMandatory}
              photosDisabled={selectedAssets.length > 1}
            />
          </div>

        </div>

        {/* Right Panel - Location Changes */}
        <div>
          <CheckinSidePanel
            formData={formData}
            sites={sites}
            departments={departments}
            locations={locations}
            onSiteChange={handleSiteChange}
            onLocationChange={handleLocationChange}
            onDepartmentChange={handleDepartmentChange}
            onNotesChange={handleNotesChange}
          />
        </div>
      </div>

      {/* Email Section and Action Buttons */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <CheckinEmailSection
          sendEmail={formData.sendEmail}
          emailAddresses={formData.emailAddresses}
          onChange={handleInputChange}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        justifyContent: 'flex-end',
        paddingTop: '16px',
        borderTop: '1px solid #e2e8f0'
      }}>
        <button
          type="button"
          onClick={() => navigate('/admin/checkin')}
          style={{
            padding: '10px 20px',
            background: '#e2e8f0',
            color: '#4a5568',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px'
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || selectedAssets.length === 0}
          style={{
            padding: '10px 20px',
            background: selectedAssets.length === 0 ? '#cbd5e0' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: selectedAssets.length === 0 ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '13px',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Processing...' : 'Check-In'}
        </button>
      </div>
    </div>
  );
};

export default CheckinFormPage;

